import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import './Circuits.css';
import {
    IconTrack, IconFlag, IconRuler, IconWidth, IconSearch, IconEdit, IconSave,
    IconX, IconTrash, IconInfo, IconLayers, IconPlus, IconBox, IconBack,
} from '../components/circuit/icons';
import { rotateTrack, nearestTrackIndex } from '../components/circuit/geometry';
import { MiniTrack, TrackCanvas } from '../components/circuit/TrackCanvas';
import { SectorEditor, BoxEditor, FinishEditor } from '../components/circuit/editors';

/* ===== Card singola con lazy-preview ===== */
function CircuitCard({ apiBase, circuit, onOpenDetail, onDelete, isDeleting = false }) {
    const [previewPts, setPreviewPts] = useState(null);
    const [previewBorders, setPreviewBorders] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const cardRef = useRef(null);

    useEffect(() => {
        const el = cardRef.current;
        if (!el) return;
        let aborted = false;
        const controller = new AbortController();
        const io = new IntersectionObserver((entries) => {
            const v = entries[0]?.isIntersecting;
            if (!v) return;
            io.disconnect();
            setIsLoading(true);
            fetch(`${apiBase}/api/circuits/${encodeURIComponent(circuit.id)}`, { signal: controller.signal })
                .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
                .then(j => {
                    if (aborted) return;
                    const pts = (Array.isArray(j.sectors) && j.sectors.length
                        ? j.sectors.map(s => ({ lat: s.lat, lon: s.lon }))
                        : Array.isArray(j.pathPoints) ? j.pathPoints : null);
                    setPreviewPts(pts);
                    if (Array.isArray(j.leftBorder) && j.leftBorder.length > 2 && Array.isArray(j.rightBorder) && j.rightBorder.length > 2) {
                        setPreviewBorders({ left: j.leftBorder, right: j.rightBorder });
                    }
                })
                .catch(() => { })
                .finally(() => !aborted && setIsLoading(false));
        }, { rootMargin: '120px' });
        io.observe(el);
        return () => { aborted = true; controller.abort(); io.disconnect(); };
    }, [apiBase, circuit.id]);

    const handleDeleteClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        onDelete?.();
    };

    return (
        <article
            ref={cardRef}
            className="rs-card rs-card--interactive circ-card"
            role="button"
            tabIndex={0}
            onClick={() => onOpenDetail(circuit.id)}
            onKeyDown={(e) => e.key === 'Enter' && onOpenDetail(circuit.id)}
        >
            <div className="circ-card__preview">
                <MiniTrack points={previewPts || []} leftBorder={previewBorders?.left} rightBorder={previewBorders?.right} className={isLoading ? 'is-loading' : ''} />
                {circuit.readOnly ? (
                    <span className="rs-badge circ-card__shared" title="Circuito della libreria condivisa: si usa nelle sessioni ma non si modifica">Libreria</span>
                ) : (
                <button
                    className="rs-btn rs-btn--sm rs-btn--danger circ-card__delete"
                    type="button"
                    onClick={handleDeleteClick}
                    disabled={isDeleting}
                    title="Elimina circuito"
                >
                    <IconTrash size={13} />
                    <span>{isDeleting ? 'Elimino…' : 'Elimina'}</span>
                </button>
                )}
            </div>

            <div className="circ-card__body">
                <div className="circ-card__head">
                    <h3 className="circ-card__title">{circuit.name}</h3>
                    <span className="circ-card__date">
                        {circuit.createdAt ? new Date(circuit.createdAt).toLocaleDateString('it-IT') : '-'}
                    </span>
                </div>

                <div className="circ-card__meta">
                    <span className="rs-chip">
                        <IconFlag size={12} />
                        <strong>{circuit.points ?? circuit.meta?.points ?? '?'}</strong> punti
                    </span>
                    {circuit.lengthMeters != null && (
                        <span className="rs-chip">
                            <IconRuler size={12} />
                            <strong>{circuit.lengthMeters.toFixed(0)}</strong> m
                        </span>
                    )}
                    {circuit.widthMeters != null && (
                        <span className="rs-chip">
                            <IconWidth size={12} />
                            <strong>{circuit.widthMeters}</strong> m
                        </span>
                    )}
                </div>
            </div>
        </article>
    );
}

/* ===== Pagina ===== */
export default function CircuitsPage({ apiBase }) {
    const navigate = useNavigate();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [query, setQuery] = useState('');
    const [sort, setSort] = useState('date_desc');
    const [openId, setOpenId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState('');
    const [fbSaving, setFbSaving] = useState(false);
    const [fbMsg, setFbMsg] = useState(null); // { ok:bool, text:string }
    const [editMode, setEditMode] = useState(false);
    const [activeTab, setActiveTab] = useState('info'); // 'info' | 'sectors' | 'box' | 'finish'
    const [sectors, setSectors] = useState([]);
    const [pitboxArea, setPitboxArea] = useState([]);
    const [startFinishIndex, setStartFinishIndex] = useState(0);
    const [saving, setSaving] = useState(false);
    const [savingName, setSavingName] = useState(false);
    const [nameEdit, setNameEdit] = useState('');
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        setLoading(true);
        setError('');
        fetch(`${apiBase}/api/circuits`)
            .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then(json => setList(json || []))
            .catch(e => setError(e.message || 'Errore caricamento circuiti'))
            .finally(() => setLoading(false));
    }, [apiBase]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        let arr = list.filter(c =>
            !q ||
            `${c.name ?? ''}`.toLowerCase().includes(q) ||
            `${c.id ?? ''}`.toLowerCase().includes(q)
        );
        switch (sort) {
            case 'name_asc':
                arr = arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                break;
            case 'len_desc':
                arr = arr.sort((a, b) => (b.lengthMeters || 0) - (a.lengthMeters || 0));
                break;
            case 'date_asc':
                arr = arr.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
                break;
            default:
                arr = arr.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        }
        return arr;
    }, [list, query, sort]);

    const openDetail = (id) => {
        setOpenId(id);
        setDetail(null);
        setDetailError('');
        setDetailLoading(true);
        setEditMode(false);
        setActiveTab('info');
        setSectors([]);
        setPitboxArea([]);
        setStartFinishIndex(0);
        fetch(`${apiBase}/api/circuits/${encodeURIComponent(id)}`)
            .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then(j => {
                setDetail(j);
                setNameEdit(j.name || '');
                setSectors(j.customSectors || []);
                setPitboxArea(j.pitboxArea || []);
                setStartFinishIndex(0);
            })
            .catch(e => setDetailError(e.message || 'Errore caricamento circuito'))
            .finally(() => setDetailLoading(false));
    };

    const saveToFirebase = async () => {
        if (!openId || fbSaving) return;
        setFbSaving(true);
        setFbMsg(null);
        try {
            const res = await fetch(`${apiBase}/api/circuits/${encodeURIComponent(openId)}/firebase`, { method: 'POST' });
            const j = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
            setFbMsg({ ok: true, text: 'Salvato su Firebase' });
        } catch (e) {
            setFbMsg({ ok: false, text: e.message || 'Errore salvataggio' });
        } finally {
            setFbSaving(false);
            setTimeout(() => setFbMsg(null), 4000);
        }
    };

    const saveName = async () => {
        if (!detail) return;
        const newName = nameEdit.trim();
        if (!newName || newName === detail.name) return;
        setSavingName(true);
        try {
            const res = await fetch(`${apiBase}/api/circuits/${encodeURIComponent(detail.id)}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: newName }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setDetail(d => (d ? { ...d, name: newName } : d));
            setList(prev => prev.map(c => (c.id === detail.id ? { ...c, name: newName } : c)));
        } catch (e) {
            alert('Errore durante la rinomina: ' + e.message);
        } finally {
            setSavingName(false);
        }
    };

    const saveChanges = async () => {
        if (!detail) return;
        setSaving(true);
        try {
            const updatedCircuit = {
                ...detail,
                customSectors: sectors,
                pitboxArea: pitboxArea,
            };
            const res = await fetch(`${apiBase}/api/circuits/${encodeURIComponent(detail.id)}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedCircuit)
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setEditMode(false);
            setActiveTab('info');
            alert('Modifiche salvate con successo!');
        } catch (e) {
            alert('Errore durante il salvataggio: ' + e.message);
        } finally {
            setSaving(false);
        }
    };

    const saveStartFinish = async () => {
        if (!detail || !points.length) return;
        setSaving(true);
        try {
            const rotatedSectors = rotateTrack(points, startFinishIndex);
            const basePath = Array.isArray(detail.pathPoints) && detail.pathPoints.length > 1
                ? detail.pathPoints
                : points;
            const pathStartPoint = points[startFinishIndex];
            const pathStartIdx = nearestTrackIndex(basePath, pathStartPoint);
            const rotatedPathPoints = rotateTrack(basePath, pathStartIdx);
            const updatedCircuit = {
                ...detail,
                sectors: rotatedSectors,
                pathPoints: rotatedPathPoints,
                meta: {
                    ...(detail.meta || {}),
                    points: rotatedPathPoints.length,
                    sectors: rotatedSectors.length,
                },
                stats: {
                    ...(detail.stats || {}),
                    lengthMeters: rotatedSectors[rotatedSectors.length - 1]?.dist || detail.stats?.lengthMeters || null,
                },
                startFinish: {
                    lat: rotatedSectors[0]?.lat,
                    lon: rotatedSectors[0]?.lon,
                    updatedAt: new Date().toISOString(),
                    source: 'manual',
                },
            };
            const res = await fetch(`${apiBase}/api/circuits/${encodeURIComponent(detail.id)}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedCircuit)
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setDetail(updatedCircuit);
            setStartFinishIndex(0);
            setActiveTab('info');
            setEditMode(false);
            setList(prev => prev.map(c => c.id === detail.id ? {
                ...c,
                points: updatedCircuit.meta?.points ?? c.points,
                lengthMeters: updatedCircuit.stats?.lengthMeters ?? c.lengthMeters,
            } : c));
            alert('Traguardo aggiornato con successo!');
        } catch (e) {
            alert('Errore durante il salvataggio del traguardo: ' + e.message);
        } finally {
            setSaving(false);
        }
    };

    const points = detail ? (
        Array.isArray(detail.sectors) && detail.sectors.length
            ? detail.sectors.map(s => ({ lat: s.lat, lon: s.lon }))
            : detail.pathPoints || []
    ) : [];

    const handleDeleteCircuit = async (id, name) => {
        if (!id || deletingId) return;
        const label = name || id;
        const confirmMsg = `Sei sicuro di voler eliminare il circuito "${label}"? L'azione non e reversibile.`;
        if (typeof window !== 'undefined' && !window.confirm(confirmMsg)) return;
        setDeletingId(id);
        try {
            const res = await fetch(`${apiBase}/api/circuits/${encodeURIComponent(id)}`, { method: 'DELETE' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setList(prev => prev.filter(c => c.id !== id));
            if (openId === id) {
                setOpenId(null);
                setDetail(null);
                setDetailError('');
                setDetailLoading(false);
                setEditMode(false);
                setSectors([]);
                setPitboxArea([]);
                setStartFinishIndex(0);
            }
            if (typeof window !== 'undefined') {
                window.alert(`Circuito "${label}" eliminato.`);
            }
        } catch (err) {
            const msg = err?.message || 'Errore sconosciuto';
            if (typeof window !== 'undefined') {
                window.alert('Errore durante l\'eliminazione: ' + msg);
            }
        } finally {
            setDeletingId(null);
        }
    };

    // Calcola statistiche
    const totalLength = list.reduce((acc, c) => acc + (c.lengthMeters || 0), 0);
    const avgLength = list.length > 0 ? totalLength / list.length : 0;

    const saveButton = (onClick, label, disabled) => (
        <button className="rs-btn rs-btn--primary" onClick={onClick} disabled={disabled} type="button">
            {saving ? (
                <>
                    <span className="rs-spinner circ-spinner--btn" />
                    <span>Salvataggio…</span>
                </>
            ) : (
                <>
                    <IconSave size={16} />
                    <span>{label}</span>
                </>
            )}
        </button>
    );

    return (
        <div className="rs-page circ-page">
            {/* ═══ HEADER ═══ */}
            <header className="rs-page-header circ-header">
                <div className="rs-page-header__text">
                    <span className="rs-kicker rs-kicker--red"><span className="rs-kicker__dot" />Gestione</span>
                    <h1 className="rs-page-header__title">Circuiti</h1>
                    <p className="rs-page-header__subtitle">Tracciati GPS, settori, box e linea del traguardo.</p>
                </div>
                <div className="rs-page-header__actions circ-header__actions">
                    <div className="rs-search circ-search">
                        <IconSearch />
                        <input
                            className="rs-input"
                            type="text"
                            placeholder="Cerca per nome o ID…"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                        />
                    </div>
                    <select id="sort" className="rs-select circ-sort" value={sort} onChange={e => setSort(e.target.value)} aria-label="Ordina per">
                        <option value="date_desc">Più recenti</option>
                        <option value="date_asc">Più vecchi</option>
                        <option value="name_asc">Nome (A-Z)</option>
                        <option value="len_desc">Lunghezza</option>
                    </select>
                    <button
                        className="rs-btn rs-btn--primary"
                        onClick={() => navigate('/circuiti/crea')}
                        type="button"
                    >
                        <IconPlus size={16} />
                        <span>Nuovo circuito</span>
                    </button>
                </div>
            </header>

            {/* ═══ KPI ═══ */}
            <section className="rs-grid rs-grid--4 circ-stats" aria-label="Riepilogo circuiti">
                <div className="rs-stat rs-stat--inline">
                    <span className="rs-stat__label">Circuiti</span>
                    <span className="rs-stat__value">{list.length}</span>
                </div>
                <div className="rs-stat rs-stat--inline">
                    <span className="rs-stat__label">Lunghezza media</span>
                    <span className="rs-stat__value rs-stat__value--mono">{avgLength > 0 ? avgLength.toFixed(0) : '–'}<small>m</small></span>
                </div>
                <div className="rs-stat rs-stat--inline">
                    <span className="rs-stat__label">Totale tracciato</span>
                    <span className="rs-stat__value rs-stat__value--mono">{totalLength > 0 ? (totalLength / 1000).toFixed(1) : '–'}<small>km</small></span>
                </div>
                <div className="rs-stat rs-stat--inline">
                    <span className="rs-stat__label">In elenco</span>
                    <span className="rs-stat__value">{filtered.length}</span>
                </div>
            </section>

            {/* ═══ ELENCO ═══ */}
            <section className="rs-section circ-list">
                <div className="rs-section__head">
                    <h2 className="rs-section__title">Elenco <span className="rs-badge">{filtered.length}</span></h2>
                </div>

                {loading && (
                    <div className="circ-loading">
                        <span className="rs-spinner" />
                        <span>Caricamento circuiti…</span>
                    </div>
                )}

                {error && (
                    <div className="rs-alert rs-alert--danger">
                        <IconX size={16} />
                        <span>Errore: {error}</span>
                    </div>
                )}

                {!loading && !error && filtered.length === 0 && (
                    <div className="rs-empty">
                        <IconTrack size={28} />
                        <strong>Nessun circuito trovato</strong>
                        <p>
                            {list.length === 0
                                ? 'Nessun circuito disponibile nel sistema.'
                                : 'Prova a modificare i filtri di ricerca.'}
                        </p>
                    </div>
                )}

                {!loading && !error && filtered.length > 0 && (
                    <div className="circ-grid">
                        {filtered.map(c => (
                            <CircuitCard
                                key={c.id}
                                apiBase={apiBase}
                                circuit={c}
                                onOpenDetail={openDetail}
                                onDelete={() => handleDeleteCircuit(c.id, c.name)}
                                isDeleting={deletingId === c.id}
                            />
                        ))}
                    </div>
                )}
            </section>

            {/* ═══ DETTAGLIO CIRCUITO — MODALE A TUTTA PAGINA ═══ */}
            {openId && (
                <div className="rs-modal-backdrop circ-detail-backdrop" role="dialog" aria-modal="true">
                    <div className="rs-modal rs-modal--lg circ-detail">
                        <div className="rs-modal__head circ-detail__head">
                            <div className="circ-detail__head-left">
                                <button className="rs-btn rs-btn--ghost rs-btn--sm" onClick={() => setOpenId(null)} type="button">
                                    <IconBack size={16} />
                                    <span>Tutti i circuiti</span>
                                </button>
                                <span className="circ-detail__title-wrap">
                                    <span className="rs-kicker">Circuito</span>
                                    <span className="rs-modal__title circ-detail__title">{detail?.name || 'Circuito'}</span>
                                </span>
                            </div>
                            {detail && detail.readOnly && (
                                <div className="circ-detail__head-actions">
                                    <span className="rs-badge" title="I circuiti della libreria condivisa si usano in gara e in allenamento, ma non si modificano">Libreria condivisa · sola lettura</span>
                                    <button className="rs-btn rs-btn--ghost rs-btn--icon rs-btn--sm" onClick={() => setOpenId(null)} aria-label="Chiudi" type="button">
                                        <IconX size={16} />
                                    </button>
                                </div>
                            )}
                            {detail && !detail.readOnly && (
                                <div className="circ-detail__head-actions">
                                    {fbMsg && (
                                        <span className={`rs-badge ${fbMsg.ok ? 'rs-badge--lime' : 'rs-badge--danger'}`}>{fbMsg.text}</span>
                                    )}
                                    <button
                                        className="rs-btn rs-btn--outline rs-btn--sm"
                                        onClick={saveToFirebase}
                                        disabled={fbSaving}
                                        type="button"
                                        title="Salva questo circuito su Firebase (Firestore) per renderlo disponibile all'app"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                                        </svg>
                                        <span>{fbSaving ? 'Salvataggio…' : 'Salva su Firebase'}</span>
                                    </button>
                                    <button
                                        className="rs-btn rs-btn--outline-red rs-btn--sm"
                                        onClick={() => navigate(`/circuiti/modifica/${encodeURIComponent(openId)}`)}
                                        type="button"
                                        title="Apri l'editor per modificare bordi, cordoli, curve, settori, box e traguardo"
                                    >
                                        <IconEdit size={16} />
                                        <span>Modifica con editor</span>
                                    </button>
                                    <button className="rs-btn rs-btn--ghost rs-btn--icon rs-btn--sm" onClick={() => setOpenId(null)} aria-label="Chiudi" type="button">
                                        <IconX size={16} />
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="circ-detail__body">
                            {detailLoading && (
                                <div className="circ-loading">
                                    <span className="rs-spinner" />
                                    <span>Caricamento circuito…</span>
                                </div>
                            )}
                            {detailError && (
                                <div className="rs-alert rs-alert--danger">
                                    <IconX size={16} />
                                    <span>Errore: {detailError}</span>
                                </div>
                            )}
                            {detail && (
                                <div className="circ-detail__grid">
                                    {/* Canvas */}
                                    <div className="circ-detail__canvas">
                                        <div className="circ-detail__canvas-head">
                                            <span className="rs-card__title"><IconTrack /> Tracciato</span>
                                            <span className="circ-detail__canvas-meta">
                                                {detail.stats?.lengthMeters ? `${detail.stats.lengthMeters.toFixed(0)} m` : ''}
                                                {detail.source === 'manual' ? ' · manuale' : ''}
                                            </span>
                                        </div>
                                        <div className="circ-detail__canvas-host">
                                            <TrackCanvas
                                                points={points}
                                                sectors={sectors}
                                                pitboxArea={pitboxArea}
                                                leftBorder={detail.leftBorder}
                                                rightBorder={detail.rightBorder}
                                                kerbs={detail.kerbs}
                                                corners={detail.corners}
                                                responsive
                                            />
                                        </div>
                                    </div>

                                    {/* Pannello */}
                                    <div className="circ-detail__panel">
                                        <div className="rs-tabs circ-detail__tabs">
                                            <button
                                                className={`rs-tab ${activeTab === 'info' ? 'is-active' : ''}`}
                                                onClick={() => { setActiveTab('info'); setEditMode(false); }}
                                                type="button"
                                            >
                                                <IconInfo size={15} />
                                                <span>Info</span>
                                            </button>
                                            {!detail.readOnly && <>
                                            <button
                                                className={`rs-tab ${activeTab === 'sectors' ? 'is-active' : ''}`}
                                                onClick={() => { setActiveTab('sectors'); setEditMode(true); }}
                                                type="button"
                                            >
                                                <IconLayers size={15} />
                                                <span>Settori</span>
                                            </button>
                                            <button
                                                className={`rs-tab ${activeTab === 'box' ? 'is-active' : ''}`}
                                                onClick={() => { setActiveTab('box'); setEditMode(true); }}
                                                type="button"
                                            >
                                                <IconBox size={15} />
                                                <span>Box</span>
                                            </button>
                                            <button
                                                className={`rs-tab ${activeTab === 'finish' ? 'is-active' : ''}`}
                                                onClick={() => { setActiveTab('finish'); setEditMode(true); setStartFinishIndex(0); }}
                                                type="button"
                                            >
                                                <IconFlag size={15} />
                                                <span>Traguardo</span>
                                            </button>
                                            </>}
                                        </div>

                                        <div className={`circ-detail__content ${editMode ? 'is-editing' : ''}`}>
                                            {activeTab === 'info' && (
                                                <div className="circ-info">
                                                    {detail.readOnly ? (
                                                    <section className="circ-info__section">
                                                        <p className="circ-info__empty">
                                                            <strong>Circuito della libreria condivisa.</strong> Puoi usarlo in gara e in allenamento così com'è. Se ti serve una versione diversa, creane uno nuovo da <em>Nuovo circuito</em>.
                                                        </p>
                                                    </section>
                                                    ) : (
                                                    <section className="circ-info__section">
                                                        <h4 className="circ-info__title"><IconEdit size={14} /><span>Nome circuito</span></h4>
                                                        <div className="rs-input-group">
                                                            <input
                                                                className="rs-input"
                                                                value={nameEdit}
                                                                onChange={(e) => setNameEdit(e.target.value)}
                                                                onKeyDown={(e) => { if (e.key === 'Enter') saveName(); }}
                                                                placeholder="Nome del circuito"
                                                                maxLength={60}
                                                            />
                                                            <button
                                                                className="rs-btn rs-btn--outline"
                                                                onClick={saveName}
                                                                disabled={savingName || !nameEdit.trim() || nameEdit.trim() === detail.name}
                                                                type="button"
                                                            >
                                                                {savingName ? (
                                                                    <><span className="rs-spinner circ-spinner--btn" /><span>Salvataggio…</span></>
                                                                ) : (
                                                                    <><IconSave size={15} /><span>Rinomina</span></>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </section>
                                                    )}

                                                    <section className="circ-info__section">
                                                        <h4 className="circ-info__title"><IconRuler size={14} /><span>Dati tecnici</span></h4>
                                                        <dl className="circ-kv">
                                                            <div className="circ-kv__item">
                                                                <dt>Punti tracciato</dt>
                                                                <dd>{detail.meta?.points ?? detail.pathPoints?.length ?? '-'}</dd>
                                                            </div>
                                                            <div className="circ-kv__item">
                                                                <dt>Lunghezza</dt>
                                                                <dd>{detail.stats?.lengthMeters ? `${detail.stats.lengthMeters.toFixed(1)} m` : '-'}</dd>
                                                            </div>
                                                            <div className="circ-kv__item">
                                                                <dt>Larghezza</dt>
                                                                <dd>{detail.params?.widthMeters ? `${detail.params.widthMeters} m` : '-'}</dd>
                                                            </div>
                                                            <div className="circ-kv__item">
                                                                <dt>Spacing</dt>
                                                                <dd>{detail.params?.spacingMeters ? `${detail.params.spacingMeters} m` : '-'}</dd>
                                                            </div>
                                                            <div className="circ-kv__item">
                                                                <dt>Min quality</dt>
                                                                <dd>{detail.params?.minQual ?? '-'}</dd>
                                                            </div>
                                                            <div className="circ-kv__item">
                                                                <dt>Creato il</dt>
                                                                <dd>{detail.createdAt ? new Date(detail.createdAt).toLocaleString('it-IT') : '-'}</dd>
                                                            </div>
                                                        </dl>
                                                    </section>

                                                    <section className="circ-info__section">
                                                        <h4 className="circ-info__title"><IconLayers size={14} /><span>Settori configurati</span></h4>
                                                        {sectors.length > 0 ? (
                                                            <div className="circ-rows">
                                                                {sectors.map(s => (
                                                                    <div key={s.id} className="circ-row">
                                                                        <span className="rs-color-dot" style={{ background: s.color }} />
                                                                        <span className="circ-row__text">
                                                                            <strong>{s.name}</strong>
                                                                            <small>{s.startIdx} – {s.endIdx} · {s.endIdx - s.startIdx + 1} punti</small>
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="circ-info__empty">
                                                                <strong>Nessun settore configurato.</strong> Usa il tab "Settori" per crearli.
                                                            </p>
                                                        )}
                                                    </section>

                                                    <section className="circ-info__section">
                                                        <h4 className="circ-info__title"><IconBox size={14} /><span>Area box</span></h4>
                                                        {pitboxArea.length >= 3 ? (
                                                            <div className="circ-rows">
                                                                <div className="circ-row">
                                                                    <span className="rs-color-dot circ-dot--warning" />
                                                                    <span className="circ-row__text">
                                                                        <strong>Area box definita</strong>
                                                                        <small>{pitboxArea.length} punti poligono</small>
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <p className="circ-info__empty">
                                                                <strong>Nessuna area box definita.</strong> Usa il tab "Box" per disegnarla.
                                                            </p>
                                                        )}
                                                    </section>

                                                    <section className="circ-info__section">
                                                        <h4 className="circ-info__title"><IconFlag size={14} /><span>Traguardo</span></h4>
                                                        {points[0] ? (
                                                            <div className="circ-rows">
                                                                <div className="circ-row">
                                                                    <span className="rs-color-dot circ-dot--finish" />
                                                                    <span className="circ-row__text">
                                                                        <strong>Linea start/finish definita</strong>
                                                                        <small>
                                                                            {points[0].lat.toFixed(6)}, {points[0].lon.toFixed(6)}
                                                                            {detail.startFinish?.source === 'manual' ? ' · manuale' : ''}
                                                                        </small>
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <p className="circ-info__empty">
                                                                <strong>Nessun traguardo disponibile.</strong> Il circuito non contiene punti validi.
                                                            </p>
                                                        )}
                                                    </section>

                                                    {detail.source === 'manual' && (
                                                        <section className="circ-info__section">
                                                            <h4 className="circ-info__title"><IconLayers size={14} /><span>Curve cronometrate &amp; cordoli</span></h4>
                                                            {Array.isArray(detail.corners) && detail.corners.length > 0 ? (
                                                                <div className="circ-rows">
                                                                    {detail.corners.map(c => (
                                                                        <div key={c.id} className="circ-row">
                                                                            <span className="rs-color-dot circ-dot--lime" />
                                                                            <span className="circ-row__text">
                                                                                <strong>{c.name}</strong>
                                                                                <small>mini-settore {c.startIdx} – {c.endIdx}</small>
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                    <div className="circ-row">
                                                                        <span className="rs-color-dot circ-dot--kerb" />
                                                                        <span className="circ-row__text">
                                                                            <strong>{(detail.kerbs || []).length} cordoli</strong>
                                                                            <small>Circuito disegnato a tavolino</small>
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <p className="circ-info__empty">
                                                                    <strong>Nessuna curva cronometrata.</strong> Definisci le curve nell'editor manuale.
                                                                </p>
                                                            )}
                                                        </section>
                                                    )}

                                                    {!detail.readOnly && (
                                                    <section className="circ-info__section circ-info__danger">
                                                        <button
                                                            className="rs-btn rs-btn--danger"
                                                            type="button"
                                                            onClick={() => handleDeleteCircuit(detail.id, detail.name)}
                                                            disabled={deletingId === detail.id}
                                                        >
                                                            <IconTrash size={15} />
                                                            <span>{deletingId === detail.id ? 'Eliminazione…' : 'Elimina circuito'}</span>
                                                        </button>
                                                    </section>
                                                    )}
                                                </div>
                                            )}

                                            {activeTab === 'sectors' && (
                                                <div className="circ-editor">
                                                    <SectorEditor
                                                        totalPoints={points.length}
                                                        sectors={sectors}
                                                        onChange={setSectors}
                                                    />
                                                    <div className="circ-editor__actions">
                                                        <button className="rs-btn rs-btn--ghost" type="button" onClick={() => {
                                                            setEditMode(false);
                                                            setActiveTab('info');
                                                            setSectors(detail.customSectors || []);
                                                        }}>
                                                            Annulla
                                                        </button>
                                                        {saveButton(saveChanges, 'Salva modifiche', saving)}
                                                    </div>
                                                </div>
                                            )}

                                            {activeTab === 'box' && (
                                                <div className="circ-editor">
                                                    <BoxEditor
                                                        trackPoints={points}
                                                        pitboxArea={pitboxArea}
                                                        onChange={setPitboxArea}
                                                    />
                                                    <div className="circ-editor__actions">
                                                        <button className="rs-btn rs-btn--ghost" type="button" onClick={() => {
                                                            setEditMode(false);
                                                            setActiveTab('info');
                                                            setPitboxArea(detail.pitboxArea || []);
                                                        }}>
                                                            Annulla
                                                        </button>
                                                        {saveButton(saveChanges, 'Salva modifiche', saving)}
                                                    </div>
                                                </div>
                                            )}

                                            {activeTab === 'finish' && (
                                                <div className="circ-editor">
                                                    <FinishEditor
                                                        trackPoints={points}
                                                        selectedIndex={startFinishIndex}
                                                        widthMeters={detail.params?.widthMeters || 6}
                                                        onChange={setStartFinishIndex}
                                                    />
                                                    <div className="circ-editor__actions">
                                                        <button className="rs-btn rs-btn--ghost" type="button" onClick={() => {
                                                            setEditMode(false);
                                                            setActiveTab('info');
                                                            setStartFinishIndex(0);
                                                        }}>
                                                            Annulla
                                                        </button>
                                                        {saveButton(saveStartFinish, 'Salva traguardo', saving || startFinishIndex === 0)}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
